import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Sparkles, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${u.startsWith("/") ? u : "/" + u}`;
}

/** Compact horizontal carousel of suggested artists (Instagram-style).
 *   - `endpoint`  : data source. Default `/discover/suggestions`. Use `/users/{id}/similar` on profile pages.
 *   - `title`     : header (defaults to translation).
 *   - `limit`     : number of cards.
 */
export default function SuggestedArtists({ endpoint = "/discover/suggestions", title, limit = 12 }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(endpoint, { params: { limit } });
        setItems(data);
      } finally { setLoading(false); }
    })();
  }, [endpoint, limit]);

  const toggleFollow = async (id) => {
    try {
      const { data } = await api.post(`/users/${id}/follow`);
      setItems((arr) => arr.map((a) => a.id === id ? { ...a, is_following: data.following, followers: data.followers } : a));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (!loading && items.length === 0) return null;

  return (
    <section className="anim-in" data-testid={`suggested-${endpoint.replace(/^\//, "").replace(/\//g, "-")}`}>
      <div className="flex items-center gap-2 mb-3 px-1">
        <Sparkles className="w-3.5 h-3.5 text-[#FFD60A]" />
        <h3 className="text-xs font-black font-display text-white uppercase tracking-wider">
          {title || (t.discover?.suggestedForYou ?? "Artistes suggérés pour toi")}
        </h3>
      </div>

      <div className="flex gap-2 overflow-x-auto scroll-hide snap-x snap-mandatory pb-2 -mx-1 px-1">
        {items.map((a) => (
          <article
            key={a.id}
            className="snap-start shrink-0 w-[120px] rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition overflow-hidden"
            data-testid={`suggested-artist-${a.id}`}
          >
            <Link to={`/profile/${a.id}`} className="block">
              <div className="aspect-[4/5] relative">
                {a.cover ? (
                  <img src={resolveImg(a.cover)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#FF0050] via-[#8A2BE2] to-[#00F2FE]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
                <div className="absolute bottom-1.5 left-1.5 right-1.5">
                  {a.avatar ? (
                    <img src={resolveImg(a.avatar)} alt={a.name} className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full grid place-items-center bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] text-white font-black border-2 border-white text-xs">
                      {a.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
              <div className="px-2 pt-1.5 pb-1">
                <p className="text-white text-[11px] font-black truncate leading-tight">{a.name}</p>
                <p className="text-[9px] text-zinc-500 truncate leading-tight">
                  {[a.country, a.genre].filter(Boolean).join(" · ") || "\u00A0"}
                </p>
              </div>
            </Link>
            <button
              onClick={() => toggleFollow(a.id)}
              className={`w-full py-1.5 text-[10px] font-black transition ${a.is_following ? "bg-white/5 text-zinc-300 hover:bg-white/10" : "bg-white text-black hover:opacity-90"}`}
              data-testid={`suggested-follow-${a.id}`}
            >
              {a.is_following ? (
                <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> {t.profile?.following ?? "Suivi(e)"}</span>
              ) : (
                <span className="inline-flex items-center gap-1"><UserPlus className="w-3 h-3" /> {t.profile?.follow ?? "Suivre"}</span>
              )}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
